import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardService } from '../../services/dashboardService';
import { getFileTypeIcon } from '../Documents/fileTypeIcons';
import { FolderIcon, LinkIcon, DocumentIcon } from '@heroicons/react/24/outline';

interface RecentDocument {
  id: string;
  title: string;
  description?: string;
  path: string;
  status: string;
  fileExtension: string;
  nodeType: string;
  createdAt: string;
  updatedAt: string;
  authorName?: string;
  categoryName?: string;
  fileSize: number;
}

export const RecentDocuments: React.FC = () => {
  const [documents, setDocuments] = useState<RecentDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchRecentDocuments();
  }, []);

  const fetchRecentDocuments = async () => {
    try {
      setIsLoading(true);
      const response = await dashboardService.getRecentDocuments(3);
      setDocuments(response);
      setError(null);
    } catch (err) {
      setError('Não foi possível carregar os documentos recentes.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerTodos = () => {
    navigate('/documents');
  };

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Agora mesmo';
    if (diffInHours === 1) return 'Há 1 hora';
    if (diffInHours < 24) return `Há ${diffInHours} horas`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) return 'Há 1 dia';
    return `Há ${diffInDays} dias`;
  };

  const getFileIcon = (fileExtension: string, nodeType: string) => {
    // Primeiro verificar o NodeType
    const nodeTypeLower = nodeType?.toLowerCase();
    
    if (nodeTypeLower === 'folder') return <FolderIcon className="h-6 w-6 text-yellow-500" />;
    if (nodeTypeLower === 'link') return <LinkIcon className="h-6 w-6 text-blue-500" />;
    if (nodeTypeLower === 'edoc') return <DocumentIcon className="h-6 w-6 text-blue-500" />;
    
    // Se não for um tipo específico, usar a extensão do arquivo
    const ext = fileExtension.toLowerCase();
    
    // Usar os ícones SVG do fileTypeIcons
    const icon = getFileTypeIcon(ext);
    
    if (icon) {
      return React.cloneElement(icon as React.ReactElement, { className: "h-6 w-6" });
    }
    
    // Para tipos não reconhecidos, usar ícone genérico
    return <DocumentIcon className="h-6 w-6 text-gray-500" />;
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Recentes</h2>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => {
              fetchRecentDocuments();
            }}
            className="text-xs px-2 py-1 bg-blue-100 text-blue-600 rounded hover:bg-blue-200"
          >
            Atualizar
          </button>
          <button 
            onClick={handleVerTodos}
            className="text-blue-600 text-sm hover:underline hover:text-blue-800 transition-colors"
          >
            Ver todos
          </button>
        </div>
      </div>
      
      {isLoading ? (
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : error ? (
        <div className="text-center text-red-500 py-4">{error}</div>
      ) : documents.length === 0 ? (
        <div className="text-center text-gray-500 py-8">
          <p>Nenhum documento recente encontrado</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {documents.map((doc) => {
            return (
              <li key={doc.id} className="pb-3 border-b border-gray-100 last:border-b-0">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">{getFileIcon(doc.fileExtension, doc.nodeType)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-gray-900 truncate" title={doc.title}>
                        {doc.title}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 mt-1">
                      {doc.categoryName && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {doc.categoryName}
                        </span>
                      )}
                      <span className="text-xs text-gray-500 uppercase">
                        {doc.fileExtension}
                      </span>
                    </div>
                    <div className="mt-2">
                      <p className="text-xs text-gray-500">
                        {doc.authorName && `por ${doc.authorName} • `}
                        {formatTimeAgo(doc.updatedAt)}
                      </p>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}; 